import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { serviceCreateSchema, type ServiceCreate } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { FieldError, Input, Label, Select, Textarea } from '../../components/ui/field';
import { api } from '../../shared/api';
import { copy, labelForServiceCategory } from '../../shared/i18n';

type ServiceFormInput = z.input<typeof serviceCreateSchema>;
type ServiceFormOutput = z.output<typeof serviceCreateSchema>;

export function ServiceCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<ServiceFormInput, unknown, ServiceFormOutput>({ resolver: zodResolver(serviceCreateSchema), defaultValues: { name: '', description: '', category: 'boarding', active: true } });
  const createService = useMutation({
    mutationFn: (values: ServiceCreate) => api.services.create(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['services'] });
      navigate('/admin/services');
    },
  });
  return (
    <ServiceFormShell title={copy.services.newService}>
      <form className="grid gap-3" onSubmit={form.handleSubmit((values) => createService.mutate(values))}>
        <ServiceFields form={form} />
        {createService.isError ? <p className="text-sm text-rose-600">{createService.error.message}</p> : null}
        <Button type="submit" disabled={createService.isPending}><Plus className="h-4 w-4" aria-hidden="true" />{copy.services.create}</Button>
      </form>
    </ServiceFormShell>
  );
}

export function ServiceFormShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-2xl space-y-5">
      <div><h1 className="text-2xl font-semibold text-slate-950">{title}</h1><p className="mt-1 text-sm text-slate-500">{copy.services.body}</p></div>
      <Card><CardHeader><h2 className="text-sm font-semibold">Datos del servicio</h2></CardHeader><CardContent>{children}</CardContent></Card>
    </div>
  );
}

export function ServiceFields({ form }: { form: ReturnType<typeof useForm<ServiceFormInput, unknown, ServiceFormOutput>> }) {
  return (
    <>
      <div className="grid gap-1.5"><Label htmlFor="name">{copy.common.name}</Label><Input id="name" {...form.register('name')} /><FieldError message={form.formState.errors.name?.message} /></div>
      <div className="grid gap-1.5">
        <Label htmlFor="category">{copy.common.category}</Label>
        <Select id="category" {...form.register('category')}>
          {(['daycare', 'boarding', 'grooming', 'spa', 'transportation', 'medical', 'treats', 'fee', 'other'] as const).map((category) => (
            <option key={category} value={category}>{labelForServiceCategory(category)}</option>
          ))}
        </Select>
      </div>
      <div className="grid gap-1.5"><Label htmlFor="description">{copy.common.description}</Label><Textarea id="description" {...form.register('description')} /></div>
    </>
  );
}
